import { directionFor } from './direction'
import { formatMessage, type MessageValues } from './format-message'
import type { Messages } from './types'

/**
 * Resolve a dotted key path against a message catalogue.
 *
 * Returns `undefined` when the path is absent or lands on a non-string, so the
 * caller can decide what to do about it. Kept separate from `translate` because
 * the fallback chain needs to run this twice — once against the active locale,
 * once against English.
 */
function lookup(messages: Messages | undefined, key: string): string | undefined {
  if (!messages) return undefined
  let value: unknown = messages
  for (const segment of key.split('.')) {
    if (typeof value !== 'object' || value === null) return undefined
    value = (value as Record<string, unknown>)[segment]
    if (value === undefined) return undefined
  }
  return typeof value === 'string' ? value : undefined
}

let nonStringKeyReported = false

/**
 * Tell the developer, once per page load, that t() was handed something that
 * is not a key. Never in production: an admin's console is not where a missing
 * map entry gets fixed, and a render loop would repeat it on every frame.
 */
function reportNonStringKey(key: unknown): void {
  if (nonStringKeyReported || process.env.NODE_ENV === 'production') return
  nonStringKeyReported = true
  console.error(
    '[i18n] t() was called with a key that is not a string, and rendered nothing. ' +
      'A map lookup probably missed (t(MAP[value]) for a value the map has no entry for). Received:',
    key,
  )
}

export interface TranslateOptions {
  /** The language `messages` is written in (the page's language). Default 'en'. */
  locale?: string
  /**
   * The locale numbers are grouped in when it differs from `locale`: the
   * provider sets it to the browser's English region on an English page
   * (en-IN groups 1,38,000). Unset, numbers follow the string's language.
   */
  numberLocale?: string
  /** Isolate interpolated values; defaults to true when `locale` reads right-to-left. */
  isolate?: boolean
}

/**
 * Translate `key`, falling back to English before falling back to the key itself.
 *
 * WHY THE ENGLISH STEP EXISTS. `t()` used to return the raw dotted key path the
 * moment a lookup missed, and English is the only catalogue every new string is
 * added to. On 2026-08-12 that meant `common.undo`,
 * `recipients.bulkDeleteGroupsConfirmDesc`, `recipients.thisGroup`,
 * `toast.cannotMergeIntoDynamic` and `toast.cannotMergeIntoDynamicDesc` existed
 * in `en.json` and in none of `ar` / `fr` / `hi` — so an Arabic, French or Hindi
 * user read the literal text "common.undo" on a button. English is a worse
 * translation than Arabic; it is a far better button label than "common.undo".
 *
 * This does not make catalogue drift acceptable, it makes it non-user-facing.
 * `i18n-english-fallback.spec.ts` pins the behaviour.
 *
 * GRAMMAR FOLLOWS THE STRING, ISOLATION FOLLOWS THE PAGE. An English fallback
 * string is pluralised with English rules (Hindi and French call 0 "one", which
 * would print "0 recipient"), but on an Arabic page its values are still
 * isolated, because the surrounding paragraph is right-to-left.
 */
export function translate(
  messages: Messages | undefined,
  englishMessages: Messages,
  key: string,
  params?: MessageValues,
  options: TranslateOptions = {},
): string {
  // A KEY THAT IS NOT A STRING IS A DISPLAY GAP, NOT A CRASH. The type says
  // `string`; the value is often `MAP[somethingTheApiSent]`, which is
  // `undefined` the day the API sends a member this build has no entry for.
  // `key.split` would then throw during render and take the page down (found
  // in review on 2026-10-02, before it happened: t(BASIS_CAPTION_KEY[data.basis])
  // on the Geography report, where the API sends three bases today).
  // The call site should still guard its own lookup: it knows whether the raw
  // value is worth showing. This is the floor under the ones that do not.
  if (typeof key !== 'string') {
    reportNonStringKey(key)
    return ''
  }

  const locale = options.locale ?? 'en'
  const isolate = options.isolate ?? directionFor(locale) === 'rtl'
  const { numberLocale } = options

  const localised = lookup(messages, key)
  if (localised !== undefined) return formatMessage(localised, params, { locale, numberLocale, isolate })

  const english = lookup(englishMessages, key)
  if (english !== undefined) return formatMessage(english, params, { locale: 'en', numberLocale, isolate })

  return key
}
